import {
  ChangeDetectionStrategy,
  Component, EventEmitter, Input, OnDestroy,
  OnInit, Output,
} from '@angular/core';
import { Activity, ActivityReport, ActivityReportFilter, ActivityType, Constants } from '../../shared/models';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';

@Component({
  selector: 'sp-activity-report-display',
  templateUrl: './activity-report-display.component.html',
  styleUrls: ['./activity-report-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ActivityReportDisplayComponent implements OnInit, OnDestroy {

  @Input() activityReport: ActivityReport;
  @Input() activityReportFilter = new ActivityReportFilter();
  @Input() showFilter = true;
  @Output() filterChanged = new EventEmitter<ActivityReportFilter>();
  @Output() pageChanged = new EventEmitter<number>();
  @Output() viewProjectDetails = new EventEmitter<string>();
  @Output() viewComponentDetails = new EventEmitter<string>();
  @Output() hideFilter = new EventEmitter<void>();

  formGroup: UntypedFormGroup = new UntypedFormGroup({});
  constants = Constants;

  private subscriptions: Subscription[] = [];
  private ngOnDestroy$ = new Subject();

  constructor(private fb: UntypedFormBuilder) {
  }

  ngOnInit() {
    this.initForm();
  }

  ngOnDestroy() {
    this.ngOnDestroy$.next('ngOnDestroy');
    this.ngOnDestroy$.unsubscribe();
  }

  clearFilter() {
    this.formGroup.get('filter').setValue('');
  }

  onFiltersReset() {

    this.valueChangesUnsubscribe();
    this.formGroup.controls['filter'].setValue('');
    this.formGroup.controls['activityType'].setValue('');
    this.formGroup.controls['userId'].setValue('');
    this.formGroup.controls['category'].setValue('');
    this.formGroup.controls['dateRange'].setValue('');
    this.formGroup.controls['customDateRange'].setValue([null, null]);
    this.filterChanged.emit(new ActivityReportFilter());
    this.valueChangesSubscribe();
  }

  onHideFilter() {
    this.hideFilter.emit();
  }

  onDxPageChanged(event) {
    if (event.name === 'pageIndex') {
      this.pageChanged.emit(event.value);
    }
  }

  onViewActivityDetails(activity: Activity) {
    switch (activity.type) {
      case ActivityType.Project:
      case ActivityType.ProjectFile:
      case ActivityType.ProjectNote:
      case ActivityType.ProjectPayment:
      case ActivityType.ProjectPhoto:
        this.viewProjectDetails.emit(activity.projectId);
        break;
      default:
        this.viewComponentDetails.emit(activity.propertyComponentId);
    }
  }

  private initForm() {

    if (this.activityReportFilter === null) {
      this.activityReportFilter = new ActivityReportFilter();
    }

    this.formGroup.addControl('filter', this.fb.control(this.activityReportFilter.filter));
    this.formGroup.addControl('activityType', this.fb.control(this.activityReportFilter.activityType));
    this.formGroup.addControl('userId', this.fb.control(this.activityReportFilter.userId));
    this.formGroup.addControl('category', this.fb.control(''));
    this.formGroup.addControl('dateRange', this.fb.control(''));
    this.formGroup.addControl('customDateRange', this.fb.control([null, null]));

    this.valueChangesSubscribe();
  }

  private valueChangesSubscribe() {
    this.subscriptions.push(this.formGroup.get('filter').valueChanges.pipe(
      debounceTime(300),
      takeUntil(this.ngOnDestroy$)
    ).subscribe(filter => {
      this.activityReportFilter.filter = filter;
      this.filterChanged.emit(this.activityReportFilter);
    }));

    this.subscriptions.push(this.formGroup.get('activityType').valueChanges.subscribe(activityType => {
      this.activityReportFilter.activityType = activityType;
      this.filterChanged.emit(this.activityReportFilter);
    }));

    this.subscriptions.push(this.formGroup.get('userId').valueChanges.subscribe(userId => {
      this.activityReportFilter.userId = userId;
      this.filterChanged.emit(this.activityReportFilter);
    }));

    this.subscriptions.push(this.formGroup.get('category').valueChanges.subscribe(propertyComponentCategoryId => {
      this.activityReportFilter.propertyComponentCategoryId = propertyComponentCategoryId;
      this.filterChanged.emit(this.activityReportFilter);
    }));

    this.subscriptions.push(this.formGroup.get('dateRange').valueChanges.subscribe(dateRange => {
      this.activityReportFilter.setDate(dateRange);

      if (!this.activityReportFilter.isDateRange) {
        this.filterChanged.emit(this.activityReportFilter);
      }
    }));

    this.subscriptions.push(this.formGroup.get('customDateRange').valueChanges.subscribe((dateRange: Date[]) => {
      if (!dateRange || dateRange.length !== 2) {
        return;
      }
      this.activityReportFilter.isDateCurrentMonth = false;
      this.activityReportFilter.isDatePreviousMonth = false;
      this.activityReportFilter.isDateCurrentYear = false;
      this.activityReportFilter.isDatePreviousYear = false;
      this.activityReportFilter.isDateRange = true;
      this.activityReportFilter.startDate = dateRange[0];
      this.activityReportFilter.endDate = dateRange[1];
      this.filterChanged.emit(this.activityReportFilter);
    }));
  }

  private valueChangesUnsubscribe() {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
